// src/components/UserSideComponents/packages/PackageSuccessStep.jsx
import { CheckCircle2, Calendar, Home, ArrowRight, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PackageSuccessStep = ({ pkg, onClose }) => {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col">
      {/* Success hero */}
      <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 px-6 py-8 text-center relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-green-200/20 rounded-full -translate-y-16 translate-x-16 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-emerald-200/20 rounded-full translate-y-20 -translate-x-20 pointer-events-none" />

        <div className="relative">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 mb-4 shadow-lg shadow-green-200/50">
            <CheckCircle2 className="text-green-600 w-12 h-12" strokeWidth={2} />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">Booking Confirmed!</h2>

          <p className="text-sm text-gray-600 max-w-xs mx-auto">
            Your <span className="font-semibold text-[#ab310c]">{pkg.name}</span> package has been booked. We'll contact you shortly.
          </p>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {/* What's next */}
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            What's Next?
          </h3>
          <div className="space-y-2.5">
            {[
              {
                num: 1,
                color: "from-blue-500 to-blue-600",
                bg: "from-blue-50 to-blue-50/50",
                border: "border-blue-100/50",
                title: "Confirmation Call",
                desc: "Our team will call to confirm booking details",
              },
              {
                num: 2,
                color: "from-purple-500 to-purple-600",
                bg: "from-purple-50 to-purple-50/50",
                border: "border-purple-100/50",
                title: "Professional Assignment",
                desc: "A verified professional will be assigned",
              },
              {
                num: 3,
                color: "from-green-500 to-green-600",
                bg: "from-green-50 to-green-50/50",
                border: "border-green-100/50",
                title: "Service Delivery",
                desc: "Professional arrives at your scheduled time & location",
              },
            ].map(({ num, color, bg, border, title, desc }) => (
              <div
                key={num}
                className={`flex items-start gap-3 p-3 bg-gradient-to-br ${bg} rounded-xl border ${border}`}
              >
                <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${color} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                  <span className="text-sm font-bold text-white">{num}</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{title}</p>
                  <p className="text-xs text-gray-600 mt-0.5">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => { onClose(); navigate("/my-bookings"); }}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-100 transition"
          >
            <Calendar size={16} />
            My Bookings
          </button>
          <button
            onClick={() => { onClose(); navigate("/"); }}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-100 transition"
          >
            <Home size={16} />
            Go Home
          </button>
        </div>

        {/* Book more */}
        <div className="bg-gradient-to-br from-orange-50 to-transparent rounded-2xl p-4 text-center border border-orange-100/50">
          <p className="text-sm text-gray-600 mb-3">Need more services?</p>
          <button
            onClick={() => { onClose(); navigate("/services"); }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#ab310c] to-[#d44a20] text-white font-semibold text-sm shadow-md hover:shadow-lg transition active:scale-[0.97]"
          >
            Browse Services
            <ArrowRight size={16} />
          </button>
        </div>

        {/* Support */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <p className="text-xs text-gray-500 mb-2">Need assistance?</p>
          <a
            href="tel:+919998887776"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#ab310c] hover:opacity-80 transition"
          >
            <Phone size={14} />
            +91 999 888 7776
          </a>
        </div>
      </div>
    </div>
  );
};

export default PackageSuccessStep;